import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { Check, PaperPlaneTilt, Calendar, Users, MapTrifold, Briefcase, User, ArrowLeft, Wrench } from '@phosphor-icons/react';
import axios from 'axios';
import { API } from '../App';

const Projet = () => {
  const [searchParams] = useSearchParams();
  const [clientType, setClientType] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const typeParam = searchParams.get('type');
    if (typeParam === 'professionnel') {
      setClientType('professionnel');
    }
  }, [searchParams]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm();

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setError(null);

    try {
      const submitData = { ...data, client_type: clientType };
      await axios.post(`${API}/contact`, submitData);
      setIsSuccess(true);
      reset();
    } catch (err) {
      setError('Une erreur est survenue. Veuillez réessayer.');
      console.error('Form submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    setClientType(null);
    reset();
  };

  // Success screen
  if (isSuccess) {
    return (
      <div className="bg-white min-h-screen flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-lg"
        >
          <div className="w-20 h-20 mx-auto mb-8 rounded-full border-2 border-black flex items-center justify-center">
            <Check size={40} weight="light" className="text-black" />
          </div>
          <h1 className="font-display text-3xl lg:text-4xl text-black mb-4">
            Message envoyé
          </h1>
          <p className="text-gray-600 mb-8">
            Merci pour votre message. Notre équipe vous contactera dans les plus brefs délais.
          </p>
          <button
            onClick={() => {
              setIsSuccess(false);
              setClientType(null);
            }}
            className="btn-primary-dark"
          >
            Envoyer un autre message
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen">
      {/* Hero with Background Image */}
      <section className="relative min-h-[60vh] flex items-center justify-center">
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets.emergentagent.com/job_signature-studio-4/artifacts/a091ai8e_Capture%20d%E2%80%99e%CC%81cran%202026-05-11%20a%CC%80%2011.02.15.png"
            alt="Studio Bar"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto text-center px-6 lg:px-12 py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white mb-4">
              Parlez-nous de votre projet
            </h1>
            <div className="w-16 h-px bg-white/50 mx-auto mt-6"></div>
            <p className="text-lg text-white/70 mt-6 max-w-2xl mx-auto">
              Nous vous accompagnons dans la conception d'une prestation cocktail, d'un bar scénographié ou d'une demande professionnelle dédiée.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-20 px-6 lg:px-12 bg-white">
        <div className="max-w-3xl mx-auto">
          <AnimatePresence mode="wait">
            {/* Client Type Selection */}
            {!clientType && (
              <motion.div
                key="selection"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
                className="text-center"
              >
                <h2 className="font-display text-2xl lg:text-3xl text-black mb-4">
                  Vous êtes...
                </h2>
                <p className="text-gray-500 mb-12">
                  Sélectionnez votre profil pour accéder au formulaire adapté
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setClientType('particulier')}
                    className="group p-10 border-2 border-black hover:bg-black transition-all duration-300"
                    data-testid="client-type-particulier"
                  >
                    <User size={48} weight="thin" className="mx-auto mb-6 text-black group-hover:text-white transition-colors" />
                    <h3 className="font-display text-2xl text-black group-hover:text-white transition-colors mb-3">
                      Particulier
                    </h3>
                    <p className="text-gray-500 group-hover:text-white/70 transition-colors text-sm">
                      Mariage, anniversaire, soirée privée, événement personnel
                    </p>
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setClientType('professionnel')}
                    className="group p-10 border-2 border-black hover:bg-black transition-all duration-300"
                    data-testid="client-type-professionnel"
                  >
                    <Briefcase size={48} weight="thin" className="mx-auto mb-6 text-black group-hover:text-white transition-colors" />
                    <h3 className="font-display text-2xl text-black group-hover:text-white transition-colors mb-3">
                      Professionnel
                    </h3>
                    <p className="text-gray-500 group-hover:text-white/70 transition-colors text-sm">
                      Location, partenariat, demande technique, événement corporate
                    </p>
                  </motion.button>
                </div>
              </motion.div>
            )}

            {/* Particulier Form */}
            {clientType === 'particulier' && (
              <motion.div
                key="particulier-form"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
              >
                <button
                  onClick={handleBack}
                  className="flex items-center gap-2 text-gray-500 hover:text-black transition-colors mb-8"
                >
                  <ArrowLeft size={18} />
                  <span>Retour</span>
                </button>

                <div className="text-center mb-12">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-full mb-6">
                    <User size={18} className="text-black" />
                    <span className="text-sm font-medium text-black">Particulier</span>
                  </div>
                  <h2 className="font-display text-2xl lg:text-3xl text-black">
                    Décrivez votre événement
                  </h2>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} data-testid="contact-form-particulier" className="space-y-8">
                  <div>
                    <h3 className="font-display text-lg text-black mb-6 pb-2 border-b border-gray-200">
                      Vos coordonnées
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm text-gray-500 mb-2">Nom *</label>
                        <input
                          type="text"
                          data-testid="input-name"
                          className={`input-underline-dark ${errors.name ? 'border-red-500' : ''}`}
                          placeholder="Votre nom"
                          {...register('name', { required: 'Le nom est requis' })}
                        />
                        {errors.name && <p className="text-red-500 text-sm mt-2">{errors.name.message}</p>}
                      </div>
                      <div>
                        <label className="block text-sm text-gray-500 mb-2">Email *</label>
                        <input
                          type="email"
                          data-testid="input-email"
                          className={`input-underline-dark ${errors.email ? 'border-red-500' : ''}`}
                          placeholder="votre@email.com"
                          {...register('email', { 
                            required: 'L\'email est requis',
                            pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: 'Adresse email invalide' }
                          })}
                        />
                        {errors.email && <p className="text-red-500 text-sm mt-2">{errors.email.message}</p>}
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm text-gray-500 mb-2">Téléphone</label>
                        <input
                          type="tel"
                          className="input-underline-dark"
                          placeholder="+33 6 12 34 56 78"
                          {...register('phone')}
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-display text-lg text-black mb-6 pb-2 border-b border-gray-200">
                      Votre événement
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm text-gray-500 mb-2">Type d'événement</label>
                        <select className="input-underline-dark bg-transparent" {...register('event_type')}>
                          <option value="">Sélectionnez...</option>
                          <option value="wedding">Mariage</option>
                          <option value="birthday">Anniversaire</option>
                          <option value="private">Soirée privée</option>
                          <option value="celebration">Célébration</option>
                          <option value="other">Autre</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-500 mb-2">
                          <span className="flex items-center gap-2">
                            <Calendar size={16} weight="light" />
                            Date de l'événement
                          </span>
                        </label>
                        <input type="date" className="input-underline-dark" {...register('event_date')} />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-500 mb-2">
                          <span className="flex items-center gap-2">
                            <MapTrifold size={16} weight="light" />
                            Lieu
                          </span>
                        </label>
                        <input
                          type="text"
                          className="input-underline-dark"
                          placeholder="Ville ou adresse"
                          {...register('event_location')}
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-500 mb-2">
                          <span className="flex items-center gap-2">
                            <Users size={16} weight="light" />
                            Nombre d'invités
                          </span>
                        </label>
                        <select className="input-underline-dark bg-transparent" {...register('guest_count')}>
                          <option value="">Sélectionnez...</option>
                          <option value="< 50">Moins de 50</option>
                          <option value="50-100">50 - 100</option>
                          <option value="100-200">100 - 200</option>
                          <option value="200-500">200 - 500</option>
                          <option value="> 500">Plus de 500</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-display text-lg text-black mb-6 pb-2 border-b border-gray-200">
                      Votre projet
                    </h3>
                    <div>
                      <label className="block text-sm text-gray-500 mb-2">Décrivez votre projet *</label>
                      <textarea
                        data-testid="input-message"
                        rows={5}
                        className={`input-underline-dark resize-none ${errors.message ? 'border-red-500' : ''}`}
                        placeholder="Décrivez votre événement, vos attentes, le style recherché..."
                        {...register('message', { required: 'Le message est requis' })}
                      />
                      {errors.message && <p className="text-red-500 text-sm mt-2">{errors.message.message}</p>}
                    </div>
                  </div>

                  {error && (
                    <div className="p-4 bg-red-50 border border-red-200 text-red-600 text-sm">{error}</div>
                  )}

                  <button
                    type="submit"
                    data-testid="submit-button"
                    disabled={isSubmitting}
                    className="btn-primary-dark w-full md:w-auto disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        Envoi en cours...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <PaperPlaneTilt size={18} weight="light" />
                        Envoyer ma demande
                      </span>
                    )}
                  </button>
                </form>
              </motion.div>
            )}

            {/* Professionnel Form */}
            {clientType === 'professionnel' && (
              <motion.div
                key="professionnel-form"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
              >
                <button
                  onClick={handleBack}
                  className="flex items-center gap-2 text-gray-500 hover:text-black transition-colors mb-8"
                >
                  <ArrowLeft size={18} />
                  <span>Retour</span>
                </button>

                <div className="text-center mb-12">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-full mb-6">
                    <Briefcase size={18} className="text-black" />
                    <span className="text-sm font-medium text-black">Professionnel</span>
                  </div>
                  <h2 className="font-display text-2xl lg:text-3xl text-black">
                    Votre demande professionnelle
                  </h2>
                  <p className="text-gray-500 mt-3">
                    Location de bars, partenariats, demandes techniques
                  </p>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} data-testid="contact-form-professionnel" className="space-y-8">
                  <div>
                    <h3 className="font-display text-lg text-black mb-6 pb-2 border-b border-gray-200">
                      Votre entreprise
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm text-gray-500 mb-2">Nom de l'entreprise *</label>
                        <input
                          type="text"
                          data-testid="input-company"
                          className={`input-underline-dark ${errors.company ? 'border-red-500' : ''}`}
                          placeholder="Nom de votre société"
                          {...register('company', { required: 'Le nom de l\'entreprise est requis' })}
                        />
                        {errors.company && <p className="text-red-500 text-sm mt-2">{errors.company.message}</p>}
                      </div>
                      <div>
                        <label className="block text-sm text-gray-500 mb-2">Secteur d'activité</label>
                        <select className="input-underline-dark bg-transparent" {...register('business_sector')}>
                          <option value="">Sélectionnez...</option>
                          <option value="event_agency">Agence événementielle</option>
                          <option value="catering">Traiteur</option>
                          <option value="wedding_planner">Wedding planner</option>
                          <option value="hotel">Hôtellerie</option>
                          <option value="venue">Lieu de réception</option>
                          <option value="production">Production audiovisuelle</option>
                          <option value="brand">Marque / Entreprise</option>
                          <option value="other">Autre</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-500 mb-2">Nom du contact *</label>
                        <input
                          type="text"
                          data-testid="input-name"
                          className={`input-underline-dark ${errors.name ? 'border-red-500' : ''}`}
                          placeholder="Votre nom"
                          {...register('name', { required: 'Le nom est requis' })}
                        />
                        {errors.name && <p className="text-red-500 text-sm mt-2">{errors.name.message}</p>}
                      </div>
                      <div>
                        <label className="block text-sm text-gray-500 mb-2">Email professionnel *</label>
                        <input
                          type="email"
                          data-testid="input-email"
                          className={`input-underline-dark ${errors.email ? 'border-red-500' : ''}`}
                          placeholder="contact@entreprise.com"
                          {...register('email', { 
                            required: 'L\'email est requis',
                            pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: 'Adresse email invalide' }
                          })}
                        />
                        {errors.email && <p className="text-red-500 text-sm mt-2">{errors.email.message}</p>}
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm text-gray-500 mb-2">Téléphone</label>
                        <input
                          type="tel"
                          className="input-underline-dark"
                          placeholder="+33 1 23 45 67 89"
                          {...register('phone')}
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-display text-lg text-black mb-6 pb-2 border-b border-gray-200">
                      Type de demande
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm text-gray-500 mb-2">Nature de la demande *</label>
                        <select 
                          className={`input-underline-dark bg-transparent ${errors.event_type ? 'border-red-500' : ''}`} 
                          {...register('event_type', { required: 'Veuillez sélectionner un type de demande' })}
                        >
                          <option value="">Sélectionnez...</option>
                          <option value="rental">Location de bar(s)</option>
                          <option value="partnership">Partenariat / Collaboration</option>
                          <option value="corporate">Événement corporate</option>
                          <option value="technical">Demande technique spécifique</option>
                          <option value="quote">Devis personnalisé</option>
                          <option value="other">Autre</option>
                        </select>
                        {errors.event_type && <p className="text-red-500 text-sm mt-2">{errors.event_type.message}</p>}
                      </div>
                      <div>
                        <label className="block text-sm text-gray-500 mb-2">
                          <span className="flex items-center gap-2">
                            <Calendar size={16} weight="light" />
                            Date(s) souhaitée(s)
                          </span>
                        </label>
                        <input type="date" className="input-underline-dark" {...register('event_date')} />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-500 mb-2">
                          <span className="flex items-center gap-2">
                            <MapTrifold size={16} weight="light" />
                            Lieu de l'événement
                          </span>
                        </label>
                        <input
                          type="text"
                          className="input-underline-dark"
                          placeholder="Adresse ou ville"
                          {...register('event_location')}
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-500 mb-2">
                          <span className="flex items-center gap-2">
                            <Users size={16} weight="light" />
                            Nombre de personnes attendues
                          </span>
                        </label>
                        <select className="input-underline-dark bg-transparent" {...register('guest_count')}>
                          <option value="">Sélectionnez...</option>
                          <option value="< 50">Moins de 50</option>
                          <option value="50-100">50 - 100</option>
                          <option value="100-200">100 - 200</option>
                          <option value="200-500">200 - 500</option>
                          <option value="> 500">Plus de 500</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-display text-lg text-black mb-6 pb-2 border-b border-gray-200">
                      <span className="flex items-center gap-2">
                        <Wrench size={20} weight="light" />
                        Informations techniques
                      </span>
                    </h3>
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm text-gray-500 mb-2">Contraintes techniques du lieu</label>
                        <textarea
                          rows={3}
                          className="input-underline-dark resize-none"
                          placeholder="Accès, dimensions, alimentation électrique, points d'eau..."
                          {...register('technical_constraints')}
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-500 mb-2">Demandes techniques spécifiques</label>
                        <textarea
                          rows={3}
                          className="input-underline-dark resize-none"
                          placeholder="Dimensions particulières, personnalisation, configuration spéciale..."
                          {...register('technical_requests')}
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-display text-lg text-black mb-6 pb-2 border-b border-gray-200">
                      Détails du projet
                    </h3>
                    <div>
                      <label className="block text-sm text-gray-500 mb-2">Description de votre demande *</label>
                      <textarea
                        data-testid="input-message"
                        rows={5}
                        className={`input-underline-dark resize-none ${errors.message ? 'border-red-500' : ''}`}
                        placeholder="Décrivez votre projet, vos besoins, le contexte de votre demande..."
                        {...register('message', { required: 'Le message est requis' })}
                      />
                      {errors.message && <p className="text-red-500 text-sm mt-2">{errors.message.message}</p>}
                    </div>
                  </div>

                  {error && (
                    <div className="p-4 bg-red-50 border border-red-200 text-red-600 text-sm">{error}</div>
                  )}

                  <button
                    type="submit"
                    data-testid="submit-button"
                    disabled={isSubmitting}
                    className="btn-primary-dark w-full md:w-auto disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        Envoi en cours...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <PaperPlaneTilt size={18} weight="light" />
                        Envoyer ma demande
                      </span>
                    )}
                  </button>
                </form>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>
    </div>
  );
};

export default Projet;
